import type { SealState, World } from '../../sim/types';
import type { SiteId } from '../../sim/data/sites';
import { reefOf } from '../../sim/data/sites';
import { DEPTH_BINS, REEF_ISLANDS, ISLAND_NAMES, binHeatFallC, binSpan, binTopM, stratumFloorKm2, stratumIndex, type DepthBin, type Reef, type ReefIsland } from '../../sim/reef';
import { CORAL_RATES, episodeKilled } from '../../sim/coral';
import { FISH_LIVES, ISLAND_HARD_BOTTOM_M2, OCTOPUS, classesOf, fishCounts, monthStep, type AgeClass, type FishCounts, type FishLife, type FishSpeciesId } from '../../sim/fish';
import { HONU, HONUEA, carapaceCm, classAgeYears as turtleAgeYears, stagesOn, turtleCounts, type TurtleLife } from '../../sim/turtles';
import { tagPlaceOn, type TagSpecies } from '../../sim/tags';
import { HONU_DRAW, honuSprites } from '../turtles';
import { mantaArrivalHour, visitorId, type SpeciesVisitNote, type VisitOdds, type Visitor } from './visitors';
import { VISIT_DAYS, visitOn, type Visit, type VisitKind } from '../persistence';
import { SPECIES_VISITS, crossingOf, humpbacksPerM2, visitorCountOn, type VisitHabitat, type VisitorSpecies } from '../../sim/visits';
import { FISH_KINDS } from '../fishKinds';
import type { CountedReefFishId } from '../fishSchools';
import { MANTA, siteHasReefResidents } from '../../sim/reefResidents';
import { REEF_SURVEYS } from '../../sim/data/fish/reefSurveys';
import { SEAL_BODY, sealStages, type SealStage } from '../seals';
import { MONK_SEAL, SEAL_DIVES_BELOW_30M, mauiNuiShare } from '../../sim/seals';
import { TIGER_SHARK, TIGER_SHARK_OFF_SHELF, lengthAtAge, offShelfSized, sharkAgeYears, type SharkLife } from '../../sim/sharks';
import { WHITETIP_SHARK } from '../../sim/whitetips';
import { HUMPBACK, whaleCounts } from '../../sim/whales';
import { WHALE_DRAW, humpbackSprites } from '../whales';
import { PELAGIC_SCENE } from './pelagicScene';
import { degreeHeatingWeeks } from '../../sim/heatStress';
import { lightShare } from '../../sim/light';
import { epochDayOf, isoDate } from '../../sim/calendar';
import { sanctuaryChlorophyll, sanctuaryPlankton } from '../../sim/systems/plankton';
import { hash3 } from '../fishSwim';
import { civilDuskHour } from '../sunlight';
import { noonUtc, solarDeclinationDeg } from '../../sim/astronomy';
import type { Place } from '../../ui/visit';
import { SPINNER_DOLPHIN, dolphinReefOdds, dolphinsPerGroupHa, siteHasSpinnerDolphins } from '../../sim/dolphins';
import { DEEP7_SPECIES, deep7FishPerHa, deep7MeanSize, type Deep7SpeciesId } from '../../sim/bottomfish';
import { PREY_SIZE, PREY_SIZE_BIN_MM, reefPredation } from '../../sim/predation';
import { REEF_SPECIES } from '../../sim/systems/fish';
import { reefGear, type GearPiece } from '../../ui/derelictGear';

/**
 * The habitat scenes' data adapter (M110, the user's third brief, section 11: "a clean interface between simulation
 * state and habitat rendering"; "Where the simulation only has broad regional averages, expose those values as
 * regional or representative estimates. Document the scope and spatial resolution of each field."). The simulation
 * says what exists and how much; this says which of it a scene is given, each figure with how far its reach goes
 * (`Scope`); the scene draws it; nothing here or there writes to the world. Pure, tested in node
 * (`tests/habitatData.test.ts`).
 *
 * **What the simulation resolves, and so what a place can be.** Maui Nui's coral is held for each island's reef in
 * each of three depth bands, 0 to 6, 6 to 18 and 18 to 30 m (a stratum, NOAA's surveys' own, `reef.ts`: 12 of them),
 * its bleaching for each band over the whole sanctuary; its reef fish for each island, by month of age (`fish.ts`);
 * its water, light and plankton for the whole sanctuary (`Environment`, `plankton.ts`); its humpbacks for all of
 * Hawaii's waters (no share of them Maui Nui's is read, M101). So a reef's place is a stratum, an island's reef at a
 * depth, and nothing finer. The Reef is one habitat (M118, `mauiNuiReefData`): the whole reef, drawn from every stratum
 * by its share of the reef's floor, its fish an average hectare's of it (`REEF_AREA_M2`), its water the sanctuary's; no
 * fine-scale coral or fish of their own is made up.
 */

/**
 * How far a figure reaches: its own stratum's, its depth band's across the sanctuary, its island's, the sanctuary's,
 * its ocean region's (a site visited, whose plankton the simulation holds only for its region), or all of Hawaii's
 * waters.
 */
export type Scope = 'stratum' | 'depth band' | 'island' | 'sanctuary' | 'region' | 'Hawaii';

/** A figure with its reach. */
export interface Scoped<T> {
  value: T;
  scope: Scope;
}

/**
 * The area a reef scene's fish stand for, m² (JUDGED, the third brief's own example: "the Reef scene might show a 100 ×
 * 100 m local reef environment"): a hectare, its fish an average hectare's of Maui Nui's reef (M118).
 */
export const REEF_AREA_M2 = 10_000;

/** A reef's place: a stratum, its island's reef in a depth band, as the simulation holds it. */
export interface ReefLocation {
  /** Its id, `maui-mid`: the island's and the band's. */
  id: string;
  island: ReefIsland;
  islandIndex: number;
  bin: DepthBin;
  binIndex: number;
  /** "Maui, 6 to 18 m". */
  name: string;
  /** Its reef floor, km² (GEBCO's, to 30 m, `MAUI_NUI_REEF`). */
  floorKm2: number;
  depthRangeM: readonly [number, number];
}

/** The reef's places a sanctuary holds: every stratum with floor, in `REEF_ISLANDS` then `DEPTH_BINS` order. */
export function reefLocations(reef: Reef): ReefLocation[] {
  const floors = stratumFloorKm2(reef);
  const out: ReefLocation[] = [];
  REEF_ISLANDS.forEach((island, i) => {
    DEPTH_BINS.forEach((bin, k) => {
      const floorKm2 = floors[stratumIndex(i, k)] ?? 0;
      if (floorKm2 <= 0) return;
      out.push({
        id: `${island}-${bin.bin}`, island, islandIndex: i, bin: bin.bin, binIndex: k,
        name: `${ISLAND_NAMES[island]}, ${binSpan(k)}`,
        floorKm2, depthRangeM: [binTopM(k), bin.bottomM],
      });
    });
  });
  return out;
}

/** The water a scene stands in, each figure the sanctuary's (one for all its waters), at its depth where it has one. */
export interface HabitatEnvironment {
  /** The sea's surface, °C, and its departure from the day's normal. */
  seaSurfaceC: Scoped<number>;
  seaSurfaceAnomalyC: Scoped<number>;
  /** The water's clarity near the top: its PAR's attenuation, m⁻¹. */
  kdPar: Scoped<number>;
  /** The day's sunlight at the surface, mol photons m⁻² d⁻¹. */
  surfaceParMol: Scoped<number>;
  /** The share of the surface's light that reaches the scene's depth (`lightShare` at it), 0 to 1. */
  lightAtDepth: Scoped<number>;
  /** The mixed layer's chlorophyll, mg m⁻³, and its zooplankton, mmol N m⁻³ (the simulated plankton's), and how deep it is, m. */
  chlorophyllMgM3: Scoped<number>;
  zooMmolNPerM3: Scoped<number>;
  mixedLayerM: Scoped<number>;
  dayLengthHours: Scoped<number>;
}

/** A reef fish's adults in a reef scene's area, at its island's density there. */
export interface SceneFish {
  species: FishSpeciesId;
  /** Its initial phase's adults and its terminal males in the scene's area, whole fish (each its island's density times the area, rounded). */
  initial: number;
  terminal: number;
  /** Their mean total lengths, mm, its island's. */
  lengthMm: { initial: number; terminal: number };
  /** All its adults (both phases) a hectare of its island's reef holds, the density the scene's are drawn by. */
  perHa: Scoped<number>;
}

/** The sanctuary's water at a depth, m. */
function environmentAt(world: World, place: Place, depthM: number): HabitatEnvironment {
  const env = place.env;
  // Its plankton its own where it has them, else its region's (a site visited, as the whole tank draws it, the review's).
  const plankton = sanctuaryPlankton(world, place);
  const sanctuary = <T>(value: T): Scoped<T> => ({ value, scope: 'sanctuary' });
  const own = <T>(value: T): Scoped<T> => ({ value, scope: place.plankton ? 'sanctuary' : 'region' });
  return {
    seaSurfaceC: sanctuary(env.seaSurfaceC),
    seaSurfaceAnomalyC: sanctuary(env.seaSurfaceAnomalyC),
    kdPar: sanctuary(env.kdPar),
    surfaceParMol: sanctuary(env.surfaceParMol),
    lightAtDepth: sanctuary(lightShare(env.kdPar, depthM)),
    chlorophyllMgM3: own(sanctuaryChlorophyll(world, place)),
    zooMmolNPerM3: own(plankton.zoo),
    mixedLayerM: sanctuary(env.mixedLayerM),
    dayLengthHours: sanctuary(env.dayLengthHours),
  };
}

/** The reef's fish the habitats count: every stock but the day octopus (M129), which is no fish (weighed by its age, no length to draw or tell), drawn apart (M158, `octopusesIn`). */
const REEF_FISH = FISH_LIVES.filter((life) => life.weightAtAgeDays === undefined);

/** A reef's fish in an area of an island's reef, from each species' counts on that island's stock alone (`REEF_FISH` order). */
function fishIn(counts: readonly FishCounts[], islandFloorM2: number, areaM2: number): SceneFish[] {
  return counts.map((c, s) => {
    const share = islandFloorM2 > 0 ? areaM2 / islandFloorM2 : 0;
    return {
      species: (REEF_FISH[s] as (typeof REEF_FISH)[number]).id,
      initial: Math.round(c.adults * share),
      terminal: Math.round(c.terminal * share),
      lengthMm: { initial: c.lengthMm.adults, terminal: c.lengthMm.terminal },
      perHa: { value: islandFloorM2 > 0 ? ((c.adults + c.terminal) * 10_000) / islandFloorM2 : 0, scope: 'island' },
    };
  });
}

/**
 * Maui Nui Reef (M118, the user's fifth brief, `docs/MAUI_NUI_REEF.md`: "one single immersive habitat called Maui Nui
 * Reef, representing the combined shallow reef system across Maui, Molokaʻi, Lānaʻi and Kahoʻolawe from 0–30 m"): the
 * sanctuary's whole reef as one habitat. The simulation still holds it stratum by stratum; the habitat is drawn from
 * every one of them, each by its share of the reef's floor (GEBCO's, `ReefLocation.floorKm2`), so a hectare of it
 * holds what an average hectare of Maui Nui's reef holds, and where on it each stratum's character shows is the
 * drawing's (`reefBlend.ts`), never a place of the stratum's own.
 */
export interface MauiNuiReef {
  id: 'maui-nui-reef';
  name: 'Maui Nui Reef';
  depthRangeM: readonly [number, number];
  /** Every stratum with floor (`reefLocations`), and each one's share of the reef's floor, summing to 1. */
  strata: readonly ReefLocation[];
  areaShare: readonly number[];
  /** Its drawing's seed, fixed (never a stream of the simulation's), so the reef is the same every time it is opened. */
  seed: number;
}

/** The reef as one habitat. */
export function mauiNuiReef(reef: Reef): MauiNuiReef {
  const strata = reefLocations(reef);
  const total = strata.reduce((s, l) => s + l.floorKm2, 0);
  return {
    id: 'maui-nui-reef', name: 'Maui Nui Reef', depthRangeM: [0, (DEPTH_BINS[DEPTH_BINS.length - 1] as (typeof DEPTH_BINS)[number]).bottomM],
    strata, areaShare: strata.map((l) => (total > 0 ? l.floorKm2 / total : 0)), seed: Math.floor(hash3(118, 0, 110) * 2 ** 31),
  };
}

/**
 * Whole numbers summing to the values' sum rounded, each its value's floor and the largest remainders a one more (so a
 * hectare's fish are as many as its share of the reef's adults, the islands' and the species' rounded together).
 */
export function largestRemainder(values: readonly number[]): number[] {
  const floors = values.map((v) => Math.floor(v));
  let left = Math.round(values.reduce((a, b) => a + b, 0)) - floors.reduce((a, b) => a + b, 0);
  const order = values.map((v, i) => [v - Math.floor(v), i] as const).sort((a, b) => b[0] - a[0] || a[1] - b[1]);
  for (const [, i] of order) {
    if (left <= 0) break;
    floors[i] = (floors[i] as number) + 1;
    left--;
  }
  return floors;
}

/**
 * The least total length a young fish is drawn at, mm (M125, JUDGED): out over the reef among the adults, as a diver
 * sees it; under it a recruit, sheltering in the reef's holes. The simulation holds 29 to 31 young of 10 cm or more to
 * the 13 to 14 adults on a hectare of the reef's floor, and every young roi past it (settled at 199 mm, Nadon's curve;
 * MEASURED by M125's measurer, four seeds to two years).
 */
export const YOUNG_SEEN_MM = 100;

/** A species' young of `YOUNG_SEEN_MM` or more on each island (`REEF_ISLANDS` order), and their mean total length, mm. */
function youngSeen(life: FishLife, state: { islands: readonly (readonly number[])[] }, epochDay: number): { byIsland: number[]; lengthMm: number } {
  const classes = classesOf(life)[monthStep(epochDay)] as readonly AgeClass[];
  let [all, mm] = [0, 0];
  const byIsland = state.islands.map((counts) => {
    let n = 0;
    counts.forEach((c, k) => {
      const a = classes[k];
      if (!a || a.tlMm < YOUNG_SEEN_MM) return;
      n += c * a.young;
      mm += c * a.young * a.tlMm;
    });
    all += n;
    return n;
  });
  return { byIsland, lengthMm: all > 0 ? mm / all : 0 };
}

/**
 * A predator's catch of a prey in Maui Nui Reef's hectare (M251): the fish of the prey's that the simulation expects it
 * to eat in a day there, among those the scene draws (of `YOUNG_SEEN_MM` or more); the hunts drawn are sampled from it
 * (`hunts.ts`), never more.
 */
export interface ReefHuntRate {
  predator: FishSpeciesId;
  prey: FishSpeciesId;
  /**
   * Which of the prey's drawn fish its predator takes (M252): its young (of `YOUNG_SEEN_MM` or more), its adults in their
   * first phase, or its terminal males; none for a prey drawn whole, not by phase (the octopus).
   */
  group?: 'young' | 'initial' | 'terminal';
  perDay: number;
}

/** The longest fish the predators take, mm: past the last of `PREY_SIZE`'s bins with a chance (Dierking et al. 2009's 20 cm). */
export const TAKEN_BELOW_MM = PREY_SIZE.reduce((last, x, k) => (x > 0 ? (k + 1) * PREY_SIZE_BIN_MM : last), 0);

/**
 * Each predator's expected catch of each of its prey in an area of the reef a day (M251), read from the day's own
 * predation (`reefPredation`, the tick's chances on the stocks as they stand, no draw taken: the simulation's own
 * reckoning, its deaths its own, `stepReef`'s): each island's chance on each class of the prey, times the class's fish
 * there, times the area's share of the reef; each class's catch shared among its young, its adults and its terminal
 * males by their shares of it (M252: so a hunt strikes the drawn fish of the group the simulation takes, the redlip's
 * young of 10 to 20 cm among its young drawn at their mean, M251's open item), its young only in the classes of
 * `YOUNG_SEEN_MM` or more (the smaller are recruits the scene does not draw, M125); a prey drawn whole, by its weight
 * and not its length (the octopus, every settled one drawn, `octopusesIn`), by every class and no group. Pairs and groups
 * the predator eats none of are left out.
 */
export function reefHuntsIn(fish: NonNullable<Place['fish']>, epochDay: number, share: number): ReefHuntRate[] {
  const step = monthStep(epochDay);
  return reefPredation(REEF_SPECIES, fish, epochDay).flatMap((p) => [...p.chances].flatMap(([prey, chances]) => {
    const life = FISH_LIVES.find((l) => l.id === prey);
    if (!life) return [];
    const classes = classesOf(life)[step] as readonly AgeClass[];
    const whole = life.weightAtAgeDays !== undefined;
    const by = { young: 0, initial: 0, terminal: 0, whole: 0 };
    // Each grown group's drawn length, its mean over the hectare's fish (`fishIn`'s), and its fish counted.
    const drawn = { initial: { mm: 0, n: 0 }, terminal: { mm: 0, n: 0 } };
    fish[prey].islands.forEach((counts, i) => counts.forEach((n, c) => {
      const k = classes[c] as AgeClass | undefined;
      if (k) for (const g of ['initial', 'terminal'] as const) {
        const m = n * (g === 'initial' ? k.adult : k.terminal);
        drawn[g].mm += m * k.tlMm;
        drawn[g].n += m;
      }
      const eaten = n * (chances[i]?.[c] ?? 0) * share;
      if (!k || eaten <= 0) return;
      if (whole) by.whole += eaten;
      else {
        if (k.tlMm >= YOUNG_SEEN_MM) by.young += eaten * k.young;
        by.initial += eaten * k.adult;
        by.terminal += eaten * k.terminal;
      }
    }));
    if (whole) return by.whole > 0 ? [{ predator: p.by, prey, perDay: by.whole }] : [];
    // A grown group drawn longer than any the predators take (the redlip's adults, 404 mm, its males 516, the few small
    // ones eaten drawn at their group's mean; M252's review) is not struck at, unless its life is taken at every size.
    const inReach = (g: 'young' | 'initial' | 'terminal'): boolean => g === 'young' || life.takenAtEverySize === true
      || (drawn[g].n > 0 && drawn[g].mm / drawn[g].n < TAKEN_BELOW_MM);
    return (['young', 'initial', 'terminal'] as const).flatMap((group) => (by[group] > 0 && inReach(group) ? [{ predator: p.by, prey, group, perDay: by[group] }] : []));
  }));
}

/** A reef fish in Maui Nui Reef's hectare: its adults and its young there from each island, whole fish, and their mean lengths. */
export interface ReefFish {
  species: FishSpeciesId;
  /** Its initial phase's adults, its terminal males and its young of `YOUNG_SEEN_MM` or more in the hectare, by island (`REEF_ISLANDS` order). */
  initial: readonly number[];
  terminal: readonly number[];
  young: readonly number[];
  /** Their mean total lengths, mm: the islands' by their fish. */
  lengthMm: { initial: number; terminal: number; young: number };
  /** All its adults a hectare of Maui Nui's reef holds, on average (each island's by its share of the reef's floor). */
  perHa: number;
  /** Its young of `YOUNG_SEEN_MM` or more a hectare of it holds. */
  youngPerHa: number;
  /**
   * The hectare's share of each group on each island before rounding, fish (M266): the drawing's counts are kept from day
   * to day while these stay within reach of them (`steadyCount`), so a share sitting on a rounding's edge does not add and
   * take a fish each day.
   */
  exact: { initial: readonly number[]; terminal: readonly number[]; young: readonly number[] };
}

/**
 * A reef fish the game counts and does not simulate, in Maui Nui Reef's hectare (M231), reckoned as the simulated fish
 * are (review M231's): each island's fish NOAA's divers' density on its hard bottom (`ISLAND_HARD_BOTTOM_M2`, the area
 * the density is over), the hectare's share of them its share of the reef's whole floor, rounded together
 * (`largestRemainder`); and of those, as the simulated species', its adults at any length and its young of
 * `YOUNG_SEEN_MM` or more, the smaller recruits sheltering in the reef: those of its half-mature length or more, each
 * island's divers' share of them (`REEF_SURVEYS`' `drawn`).
 */
export interface CountedReefFish {
  species: CountedReefFishId;
  byIsland: readonly number[];
  /** Its fish of every size a hectare of Maui Nui's reef's floor holds, and each island's, as the simulated fish's `perHa` are (from NOAA's divers', `REEF_SURVEYS`, MEASURED). */
  perHa: number;
  perHaByIsland: readonly number[];
  /** The divers' mean total length, mm (MEASURED), of those drawn, at which all are drawn. */
  lengthMm: number;
}

/** Everything Maui Nui Reef is given: each stratum's coral and bottom, each band's bleaching, the reef's fish, its water. */
export interface MauiNuiReefData {
  sanctuaryId: SiteId;
  habitat: 'reef';
  reef: MauiNuiReef;
  representativeAreaM2: number;
  simulationDate: string;
  epochDay: number;
  /** At the reef's middle depth by its floor (the habitat's own, `REEF_SCENE`). */
  environment: HabitatEnvironment;
  /** Each stratum's live cover, percent (in `reef.strata` order). */
  coverPercent: readonly number[];
  /**
   * Each stratum's turf and macroalgae as the simulation holds them, percent of its bottom (M177, its grazing's
   * `CoralBottom`, M167; in `reef.strata` order), the reef's paint's growths; none where the simulation holds none.
   */
  bottom?: { turfPercent: readonly number[]; macroalgaePercent: readonly number[] };
  /** Each band's bleached share, its share a heat episode under way has killed, and its Degree Heating Weeks (`DEPTH_BINS` order; the simulation holds no island's). */
  bands: readonly { bleachedPercent: number; killedPercent: number; dhw: number }[];
  ceilingPercent: number;
  /** Each island's adults a hectare of its own reef holds, by species (the card's and the panel's, `fishIn`). */
  islands: readonly { island: ReefIsland; name: string; areaShare: number; fish: SceneFish[] }[];
  fish: readonly ReefFish[];
  /** The island the scene shows as its own (M287, `focusIsland`), or none for the whole of Maui Nui. */
  focus?: ReefIsland;
  /** The reef fish counted, not simulated (M231): none where the site's divers counted none (`siteHasReefResidents`). */
  counted: readonly CountedReefFish[];
  /** The honu at home in the hectare on average: the sanctuary's, each island's share by its reef's floor. */
  honu: number;
  /** The honu and the seals visiting the scene today (M145, `visitorsOf`), and the odds each comes on a day. */
  visitors: Visitor[];
  visitOdds: VisitOdds;
  /** The species visiting from their own habitats (M147, `speciesVisits`); their drawn ones among `visitors`. */
  visits: SpeciesVisitNote[];
  /** The day octopuses in the hectare (M158, `octopusesIn`): each one's island and weight. */
  octopuses: readonly ReefOctopus[];
  /** The spinner dolphins resting over the hectare today (M216, `reefDolphinsOn`), null on a day none does. */
  dolphins: ReefDolphins | null;
  /** The odds a group rests over the hectare on a day; 0 where the site has none. */
  dolphinOdds: number;
  /** All the day octopuses a hectare of Maui Nui's reef holds, on average. */
  octopusPerHa: number;
  /** Each predator's expected catch of each prey the scene draws in the hectare today (M251, `reefHuntsIn`), the hunts' to sample. */
  hunts: readonly ReefHuntRate[];
  /** The day's light, hours from first light to last (civil twilight's, `civilDuskHour`), the octopus's forages' (M201). */
  lightHours: number;
  /** The derelict gear snagged on the reef today (M338, `reefGear`), in its fixed order: the shores' tonnes, a piece a trip's haul. */
  gear: readonly GearPiece[];
}

/**
 * A day octopus in Maui Nui Reef's hectare (M158): its own draw (the world's seed's, its island's and its place among
 * them, so its den and its days are its world's own: M149's review found the honu's visits the same in every world), its
 * island (`REEF_ISLANDS` order), its weight, kg, and whether it is not yet mature.
 */
export interface ReefOctopus {
  key: number;
  island: number;
  weightKg: number;
  young: boolean;
}

/**
 * The day octopuses in an area of the reef (M158): each island's settled animals, all of them (the simulation counts it
 * by its dens, one octopus a den, Sims 1998, `OCTOPUS.densityPerHa`), its share of them the area's of the reef's floor,
 * rounded together (`largestRemainder`); each island's given the weights its age classes hold, the k-th of n the
 * weight at its share (k + 0.5) / n through them, lightest first, so the hectare's octopuses are its classes' spread.
 */
export function octopusesIn(life: FishLife, state: { islands: readonly (readonly number[])[] }, epochDay: number, share: number, seed: number): ReefOctopus[] {
  const month = classesOf(life)[monthStep(epochDay)] as readonly AgeClass[];
  const counts = largestRemainder(state.islands.map((c) => c.reduce((a, b) => a + b, 0) * share));
  return state.islands.flatMap((byClass, island) => {
    const n = counts[island] ?? 0;
    const order = byClass.map((c, k) => ({ c, k: month[k] as AgeClass })).filter((x) => x.c > 0).sort((a, b) => a.k.weightKg - b.k.weightKg);
    const all = order.reduce((a, x) => a + x.c, 0);
    return Array.from({ length: n }, (_, j) => {
      let left = ((j + 0.5) / n) * all;
      const at = order.find((x) => (left -= x.c) < 0) ?? order[order.length - 1];
      return { key: Math.floor(hash3(seed, island, j + 158) * 2 ** 31), island, weightKg: at?.k.weightKg ?? 0, young: (at?.k.young ?? 0) > 0.5 };
    });
  });
}

/** Maui Nui Reef's data on the world's day; null where the sanctuary has no reef or no coral (a visited site). */
export function mauiNuiReefData(world: World, place: Place, depthM: number): MauiNuiReefData | null {
  const reef = reefOf(place);
  if (!reef || !place.coral) return null;
  const whole = mauiNuiReef(reef);
  const epochDay = epochDayOf(world);
  const floors = stratumFloorKm2(reef);
  const reefM2 = whole.strata.reduce((s, l) => s + l.floorKm2, 0) * 1e6;
  const islandM2 = REEF_ISLANDS.map((_, i) => DEPTH_BINS.reduce((s, _b, k) => s + (floors[stratumIndex(i, k)] ?? 0), 0) * 1e6);
  const heat = world.sanctuaries.find((s) => s.siteId === place.siteId)?.heatStress;
  const killed = episodeKilled(reef, place.coral).byBin;
  const falls = binHeatFallC(reef);
  // Each species' adults on each island, whole island, counted on the island's own cohorts (the others' none), so its
  // adults, their lengths and its density (`fishIn`) are its own; the hectare's share of them the hectare's of the whole
  // reef.
  const raw = place.fish ? REEF_FISH.map((life) => REEF_ISLANDS.map((_, i) => {
    const state = (place.fish as NonNullable<Place['fish']>)[life.id];
    return fishCounts(life, { islands: state.islands.map((counts, j) => (j === i ? counts : counts.map(() => 0))), afloat: state.afloat }, epochDay);
  })) : [];
  const young = place.fish ? REEF_FISH.map((life) => youngSeen(life, (place.fish as NonNullable<Place['fish']>)[life.id], epochDay)) : [];
  // The fish reckoned over the reef's floor to 30 m, as its coral is (the reef the floor, M45 and the user's word after
  // M48; NOAA's densities and the simulation's stocks are over its hard bottom, 269 of its 410.5 km², `MAUI_NUI_HARD_BOTTOM_KM2`).
  const fishM2 = reefM2;
  const share = REEF_AREA_M2 / Math.max(1, fishM2);
  // Each species' phases and its young rounded together first, so the hectare holds as many of each as its share of
  // the reef's, then each one's among the islands (rounded island by island, a species whose fish are spread thin would
  // round away).
  const want = raw.map((byIsland, s) => [
    byIsland.map((c) => c.adults * share),
    byIsland.map((c) => c.terminal * share),
    (young[s]?.byIsland ?? []).map((n) => n * share),
  ]);
  const totals = largestRemainder(want.flatMap((phases) => phases.map((byIsland) => byIsland.reduce((a, b) => a + b, 0))));
  const counted = want.flatMap((phases, s) => phases.map((byIsland, phase) => {
    const sum = byIsland.reduce((a, b) => a + b, 0);
    const total = totals[s * 3 + phase] as number;
    return largestRemainder(byIsland.map((x) => (sum > 0 ? (x * total) / sum : 0)));
  }));
  const fish: ReefFish[] = REEF_FISH.flatMap((life, s) => {
    const byIsland = raw[s];
    if (!byIsland) return [];
    const at = (i: number, phase: 0 | 1 | 2): number => counted[s * 3 + phase]?.[i] ?? 0;
    const mean = (key: 'adults' | 'terminal'): number => {
      const n = byIsland.reduce((a, c) => a + c[key], 0);
      return n > 0 ? byIsland.reduce((a, c) => a + c[key] * c.lengthMm[key], 0) / n : (byIsland[0]?.lengthMm[key] ?? 0);
    };
    return [{
      species: life.id,
      initial: REEF_ISLANDS.map((_, i) => at(i, 0)),
      terminal: REEF_ISLANDS.map((_, i) => at(i, 1)),
      young: REEF_ISLANDS.map((_, i) => at(i, 2)),
      lengthMm: { initial: mean('adults'), terminal: mean('terminal'), young: young[s]?.lengthMm ?? 0 },
      perHa: (byIsland.reduce((a, c) => a + c.adults + c.terminal, 0) * 10_000) / Math.max(1, fishM2),
      youngPerHa: ((young[s]?.byIsland ?? []).reduce((a, b) => a + b, 0) * 10_000) / Math.max(1, fishM2),
      exact: { initial: [...(want[s]?.[0] ?? [])], terminal: [...(want[s]?.[1] ?? [])], young: [...(want[s]?.[2] ?? [])] },
    }];
  });
  return {
    sanctuaryId: place.siteId,
    habitat: 'reef',
    reef: whole,
    representativeAreaM2: REEF_AREA_M2,
    simulationDate: isoDate(epochDay),
    epochDay,
    environment: environmentAt(world, place, depthM),
    coverPercent: whole.strata.map((l) => place.coral?.coverPercent[stratumIndex(l.islandIndex, l.binIndex)] ?? 0),
    ...(place.coral?.bottom ? { bottom: {
      turfPercent: whole.strata.map((l) => place.coral?.bottom?.turfPercent[stratumIndex(l.islandIndex, l.binIndex)] ?? 0),
      macroalgaePercent: whole.strata.map((l) => place.coral?.bottom?.macroalgaePercent[stratumIndex(l.islandIndex, l.binIndex)] ?? 0),
    } } : {}),
    bands: DEPTH_BINS.map((_, k) => ({
      bleachedPercent: place.coral?.bleachedPercent[k] ?? 0,
      killedPercent: killed[k] ?? 0,
      dhw: heat ? degreeHeatingWeeks(heat, falls[k] ?? 0) : 0,
    })),
    ceilingPercent: CORAL_RATES.ceilingPercent,
    islands: REEF_ISLANDS.map((island, i) => ({
      island, name: ISLAND_NAMES[island], areaShare: (islandM2[i] as number) / Math.max(1, reefM2),
      fish: fishIn(raw.map((byIsland) => byIsland[i] as FishCounts), islandM2[i] as number, REEF_AREA_M2),
    })),
    fish,
    counted: !place.visiting && siteHasReefResidents(place.siteId) ? [countedIn('yellowTang', REEF_SURVEYS.yellowTang, islandM2, reefM2), countedIn('moray', REEF_SURVEYS.moray, islandM2, reefM2)] : [],
    honu: honuPerReefHa(place, epochDay).all,
    ...withSpecies(world, place, epochDay, 'reef', visitorsOf(place, epochDay, 'reef')),
    visitOdds: visitOddsOf(place, epochDay, 'reef'),
    octopuses: place.fish ? octopusesIn(OCTOPUS, place.fish.octopus, epochDay, share, world.seed) : [],
    hunts: place.fish && !place.visiting ? reefHuntsIn(place.fish, epochDay, share) : [],
    octopusPerHa: place.fish ? (place.fish.octopus.islands.reduce((n, c) => n + c.reduce((a, b) => a + b, 0), 0) * 10_000) / Math.max(1, fishM2) : 0,
    lightHours: 2 * (civilDuskHour(place.site.latDeg, solarDeclinationDeg(noonUtc(epochDay))) - 12),
    gear: place.visiting ? [] : reefGear(world),
    dolphins: reefDolphinsOn(place, epochDay),
    dolphinOdds: !place.visiting && siteHasSpinnerDolphins(place.siteId) ? dolphinReefOdds() : 0,
  };
}

/** A counted reef fish in the hectare (`CountedReefFish`): each island's count on its share of the reef's floor, rounded together. */
function countedIn(species: CountedReefFishId, survey: { perHaByIsland: readonly number[]; drawn: { shareByIsland: readonly number[]; meanTlCm: number } }, islandM2: readonly number[], reefM2: number): CountedReefFish {
  const onIsland = survey.perHaByIsland.map((perHa, i) => (perHa * (ISLAND_HARD_BOTTOM_M2[i] ?? 0)) / 10_000);
  const want = onIsland.map((n, i) => (n * (survey.drawn.shareByIsland[i] ?? 0) * REEF_AREA_M2) / Math.max(1, reefM2));
  return {
    species,
    byIsland: largestRemainder(want),
    perHa: (onIsland.reduce((a, b) => a + b, 0) * 10_000) / Math.max(1, reefM2),
    perHaByIsland: onIsland.map((n, i) => ((islandM2[i] ?? 0) > 0 ? (n * 10_000) / (islandM2[i] as number) : 0)),
    lengthMm: survey.drawn.meanTlCm * 10,
  };
}

/** A group of spinner dolphins over the Reef's hectare on a day: its dolphins there, its whole, and its hours inshore. */
export interface ReefDolphins {
  count: number;
  groupMean: number;
  hours: readonly [number, number];
}

/**
 * The spinner dolphins over the Reef's hectare on a day (M216): on the day's draw of its own against the odds a group
 * rests there (`dolphinReefOdds`, one day in 282), the dolphins of a group a hectare it covers (`dolphinsPerGroupHa`,
 * 10.9, rounded by the day's own draw, up or down by its fraction), over the hours they are inshore (`inshoreHours`);
 * none at a site without them, or visited.
 */
export function reefDolphinsOn(place: Place, epochDay: number): ReefDolphins | null {
  if (place.visiting || !siteHasSpinnerDolphins(place.siteId) || !(hash3(epochDay, 216, 2160) < dolphinReefOdds())) return null;
  const perHa = dolphinsPerGroupHa();
  return { count: Math.floor(perHa) + (hash3(epochDay, 217, 2160) < perHa - Math.floor(perHa) ? 1 : 0), groupMean: SPINNER_DOLPHIN.groupMean, hours: SPINNER_DOLPHIN.inshoreHours };
}

/**
 * The open water's whales as a scene draws them: a whale for each `WHALE_DRAW.whalesPerSprite` (1,000) of the
 * population's whales in Hawaii's waters and its calves by the same, the whole tank's own proxy (the brief: "use the
 * existing relationship between displayed proxies and represented animal counts"), so at the winter's peak about nine
 * swim in the scene's water, none from June to September; Hawaii's, not Maui Nui's, as no share of them Maui Nui's is
 * read (M101).
 */
const BLUE_WHALES = { whalesPerSprite: WHALE_DRAW.whalesPerSprite } as const;

/** Everything a water scene (the open water, the twilight, the slope's, the deep's) is given. */
export interface WaterSceneData {
  sanctuaryId: SiteId;
  /** Its habitat, the whole tank's (`Habitat['id']`). */
  habitat: string;
  /** Its place: the sanctuary's water at the habitat's depths (the simulation holds no finer water). */
  location: { id: string; name: string; depthRangeM: readonly [number, number]; seed: number };
  simulationDate: string;
  epochDay: number;
  /** At its depths' middle (the blue water's at 15 m, near the surface where its whales swim). */
  environment: HabitatEnvironment;
  /** The humpbacks in Hawaii's waters and their calves there, and the population's whole; none where the sanctuary holds none or the habitat is not the open water. */
  whales: { inHawaii: Scoped<number>; calvesInHawaii: Scoped<number>; population: number; sprites: { whales: number; calves: number }; perSprite: number } | null;
  /** The animals the simulation holds that swim here but are not drawn, each with why (the brief: "a sparse but scientifically accurate ocean scene"). */
  notDrawn: string[];
  /** The honu and the seals visiting the scene today (M145, `visitorsOf`), and the odds each comes on a day. */
  visitors: Visitor[];
  /** Null at a visited site, where neither is simulated (M150's review: its panel spoke of Hawaiʻi's honu and seals). */
  visitOdds: VisitOdds | null;
  /** The species visiting from their own habitats (M147, `speciesVisits`); their drawn ones among `visitors`. */
  visits: SpeciesVisitNote[];
  /** The Slope's Deep 7 in its hectare today (M215, `slopeFishOn`); none elsewhere. */
  bottomfish: SlopeFishDay[];
}

/** A Deep 7 species in the Slope's hectare today: its fish there, its fish a hectare of the hard bottom, its mean size. */
export interface SlopeFishDay {
  species: Deep7SpeciesId;
  count: number;
  perHa: number;
  kg: number;
  forkCm: number;
}

/**
 * The Deep 7 in the Slope's scene on a day (M215): its hectare is one of Maui Nui's hard bottom at 100 to 400 m, the
 * floor the stock is spread over (`deep7FishPerHa`; the scene draws basalt ledges), each species' fish there today its
 * mean count rounded by a draw of the day's own, up or down by its fraction (so a species of 0.29 a hectare, the lehi,
 * comes on about 29 days in 100). None at a site without the stock, or visited.
 */
export function slopeFishOn(place: Place, epochDay: number): SlopeFishDay[] {
  if (place.visiting || !place.slope) return [];
  const deep7 = place.slope.deep7;
  return DEEP7_SPECIES.map((s, k) => {
    const perHa = (deep7FishPerHa(deep7, epochDay, s.id) * VISIT_AREA_M2) / 10_000;
    const size = deep7MeanSize(s.id);
    return { species: s.id, count: Math.floor(perHa) + (hash3(epochDay, k, 2150) < perHa - Math.floor(perHa) ? 1 : 0), perHa, kg: size.kg, forkCm: size.forkCm };
  });
}

/** A water scene's data on the world's day for a habitat, its name and depths the whole tank's. */
export function waterSceneData(world: World, place: Place, habitat: string, name: string, depthRangeM: readonly [number, number]): WaterSceneData {
  const epochDay = epochDayOf(world);
  const open = habitat === 'blue';
  const depth = open ? 15 : (depthRangeM[0] + depthRangeM[1]) / 2;
  let whales: WaterSceneData['whales'] = null;
  if (place.whales && open) {
    const c = whaleCounts(HUMPBACK, place.whales.humpback, epochDay);
    const drawn = humpbackSprites(c, epochDay);
    whales = {
      inHawaii: { value: c.inHawaii, scope: 'Hawaii' },
      calvesInHawaii: { value: drawn.calvesInHawaii, scope: 'Hawaii' },
      population: c.population,
      sprites: { whales: drawn.whales, calves: drawn.calves },
      perSprite: BLUE_WHALES.whalesPerSprite,
    };
  }
  const notDrawn: string[] = [];
  if (open && !place.visiting && place.siteId === 'maui_nui') {
    notDrawn.push('the Pacific blue marlin, simulated across the Pacific, with no share of it here read');
    notDrawn.push('the spinner dolphin at night, hunting offshore over the slope, with no count of Maui Nui\'s there (by day it is drawn resting on the Reef)');
  }
  const day = place.visiting ? { visitors: [], visits: [] } : withSpecies(world, place, epochDay, habitat, visitorsOf(place, epochDay, habitat));
  if (place.visiting) notDrawn.push('no wildlife is simulated at a visited site');
  // The twilight's open water holds no simulated animal, its slope's floor at Maui Nui the Deep 7, drawn on the Slope
  // since M215, and the deepwater shrimp (M192, M193; review M194: it read that nothing of these depths was simulated).
  else if (!open && habitat === 'twilight' && place.siteId === 'maui_nui') notDrawn.push('no animal of the open water at these depths is simulated yet', 'the Deep 7 bottomfish, simulated on the slope\'s floor at these depths, drawn on the Slope', 'the deepwater shrimp, simulated on the floor from 350 to 830 m, most of it at 460 to 640 m, below the Slope\'s 400 m, with no scene of that floor');
  // The Slope's own since M215: the Deep 7 at Maui Nui.
  else if (!open && !(habitat === 'slope' && place.slope)) notDrawn.push(day.visits.length ? 'no animal living only at these depths is simulated yet' : 'no animal of these depths is simulated yet');
  return {
    sanctuaryId: place.siteId,
    habitat,
    location: { id: `${place.siteId}-${habitat}`, name, depthRangeM, seed: Math.floor(hash3(habitat.length * 31 + (habitat.charCodeAt(0) || 0), 7, 110) * 2 ** 31) },
    simulationDate: isoDate(epochDay),
    epochDay,
    environment: environmentAt(world, place, depth),
    whales,
    notDrawn,
    ...day,
    visitOdds: place.visiting ? null : visitOddsOf(place, epochDay, habitat),
    bottomfish: habitat === 'slope' ? slopeFishOn(place, epochDay) : [],
  };
}

/**
 * The odds a monk seal visits a habitat's scene on a day (M145): on the Reef, 0 to 30 m, 20% (the user's word: "make
 * the seal odds much higher, like 20%"); on the Slope, 30 to 400 m since M215 (30 to 150 m when this was set), the Reef's times the seals' dives below 30 m
 * against those above it. Maui Nui's seals dive "most dives to depths of 12–32 m" (Wilson, Littnan and Read 2017,
 * *Marine Mammal Science* 33, accepted manuscript p. 2), a quarter of them below 30 m (`SEAL_DIVES_BELOW_30M`, its
 * reading there), so the Slope's odds a third of the Reef's. Nowhere else:
 * "Monk seals are benthic foragers and consequently, most dives were to the sea floor" (p. 10), "most seals remained
 * within the 200 m isobath and foraged along the island coastlines" (p. 11), so the open water's and the deep's none
 * (their crossings between islands, "some individuals visited neighboring islands or traveled to multiple islands
 * (37%)", p. 9, and the rare dives past 200 m, no time measured).
 */
export const SEAL_VISIT_ODDS: Partial<Record<string, number>> = { reef: 0.2, slope: (0.2 * SEAL_DIVES_BELOW_30M) / (1 - SEAL_DIVES_BELOW_30M) };

/**
 * The odds a seal visits a habitat's scene on a day: `SEAL_VISIT_ODDS`' at Maui Nui's seals as NOAA counted them (its
 * share of the main islands' 393 in 2024, `MONK_SEAL`), rising and falling with its seals at sea (the nursing pups ashore
 * not among them), none when none are left (M145's review: the odds were the same with no seals).
 */
export function sealVisitOdds(place: Place, habitat: string): number {
  if (!place.seals) return 0;
  const counted = MONK_SEAL.mainIslands * mauiNuiShare(MONK_SEAL);
  const n = sealStages(MONK_SEAL, place.seals.monk);
  return Math.min(1, ((SEAL_VISIT_ODDS[habitat] ?? 0) * (n.pup + n.juvenile + n.adult)) / counted);
}

/**
 * The honu's share of their year in open water (M145): Hawaii's adults cross it to breed at French Frigate Shoals, "It
 * took an average of 36 days for the turtles to make the trip to French Frigate Shoals and an average of 30 days to
 * make the return trip" (Rice and Balazs 2010, NOAA-TM-NMFS-SEFSC-602, p. 61, three adults tracked, MEASURED), each sex
 * as often as it breeds (`HONU.femaleBreeding`, `maleBreeding`), weighed by its share of the adults (`femaleShare`):
 * 0.072 of an adult's year. The Blue Water's odds a honu comes are the Reef's hectare's times the adults' share of the
 * honu at home times that share over the rest of the year (a reading: a migrant is as likely in the scene's water as
 * a resident on the reef's hectare). None on the Slope (Turtle 257's resting dives "from 4 to 38.5 meters ... Dives
 * deeper than 14 meters were uncommon", `HONU_DAY`, with no share below 30 m measured) or deeper ("The adult female
 * made two dives in excess of 135 meters", Rice and Balazs, the deepest recorded).
 */
export const HONU_OPEN_WATER_SHARE = ((HONU.femaleShare * HONU.femaleBreeding + (1 - HONU.femaleShare) * HONU.maleBreeding) * (36 + 30)) / 365;

/** A sea turtle's (the honu's by default) a hectare of Maui Nui's reef holds on a day, the Reef scene's (all its strata's floor). */
function honuPerReefHa(place: Place, epochDay: number, life: TurtleLife = HONU): { all: number; adultShare: number } {
  const reef = reefOf(place);
  if (!place.turtles || !reef) return { all: 0, adultShare: 0 };
  const t = turtleCounts(life, life === HONUEA ? place.turtles.honuea : place.turtles.honu, epochDay);
  const reefM2 = stratumFloorKm2(reef).reduce((a, b) => a + b, 0) * 1e6;
  const all = ((t.young + t.adults) * REEF_AREA_M2) / Math.max(1, reefM2);
  return { all, adultShare: t.young + t.adults > 0 ? t.adults / (t.young + t.adults) : 0 };
}

/** The odds a honu visits a habitat's scene on a day: the Reef's its hectare's honu, the Blue Water's its migrants'. */
export function honuVisitOdds(place: Place, epochDay: number, habitat: string): number {
  const h = honuPerReefHa(place, epochDay);
  if (habitat === 'reef') return Math.min(1, h.all);
  if (habitat === 'blue') return Math.min(1, (h.all * h.adultShare * HONU_OPEN_WATER_SHARE) / (1 - HONU_OPEN_WATER_SHARE));
  return 0;
}

/**
 * The odds a hawksbill visits a habitat's scene on a day (M196): the Reef's its hectare's hawksbills, as the honu's
 * are; none elsewhere, no share of a Hawaiian hawksbill's time off the reef read (M187: no foraging count or arrival
 * and departure of one found).
 */
export function honueaVisitOdds(place: Place, epochDay: number, habitat: string): number {
  return habitat === 'reef' ? Math.min(1, honuPerReefHa(place, epochDay, HONUEA).all) : 0;
}

/**
 * The habitats a tiger shark visits (M197), each at its density over Maui Nui's shelf to the 200 m isobath (the count's
 * own area, `TIGER_SHARK.mauiNuiShelfKm2`): the Reef, "Tiger sharks were detected at shallow ocean recreation sites around
 * Maui at all times of the day and night", and the Slope, "the modal bathymetry depth, and likely centre of tiger shark activity, is
 * between 50 and 100 m" (Meyer et al. 2018, pp. 9 and 2). No share of their time by depth is measured, so each the
 * same (open). The Blue Water since M211: the sharks of the sizes tracked off the shelf (`offShelfSized`, 217 cm and
 * longer) at the shelf's hectare's odds times their share of the time off it over the rest (`TIGER_SHARK_OFF_SHELF`).
 * Three readings, each open: the game's count is taken as the sharks on the shelf, those off it on top of it (the count
 * is Oʻahu's shelf's catch, a floor); a shark off the shelf as likely in the scene's water as one on the shelf in a
 * hectare of it, as the honu's migrants are, though their trips run ">100 km up to 1,460 km" (Meyer et al. 2018, p. 2),
 * so the odds are likely high; and every shark of those sizes as likely to go, where the fixes off the shelf were
 * seven sharks'.
 */
export const TIGER_SHARK_HABITATS: readonly string[] = ['reef', 'slope', 'blue'];

/** Each class's sharks of the sizes tracked off the shelf on a day (`offShelfSized`), none of the rest. */
const offShelfCounts = (counts: readonly number[], epochDay: number): number[] => counts.map((n, c) => (offShelfSized(TIGER_SHARK, c, epochDay) ? n : 0));

/** The odds a tiger shark visits a habitat's scene on a day (M197): its sharks a hectare of the shelf, where it visits, and the Blue Water's those off it (M211); none where the site has none. */
export function tigerSharkVisitOdds(place: Place, habitat: string, epochDay: number): number {
  if (!place.sharks || !TIGER_SHARK_HABITATS.includes(habitat)) return 0;
  const counts = habitat === 'blue' ? offShelfCounts(place.sharks.tiger.counts, epochDay) : place.sharks.tiger.counts;
  const n = counts.reduce((a, b) => a + b, 0);
  const shelf = (n * VISIT_AREA_M2) / (TIGER_SHARK.mauiNuiShelfKm2 * 1e6);
  return Math.min(1, habitat === 'blue' ? (shelf * TIGER_SHARK_OFF_SHELF) / (1 - TIGER_SHARK_OFF_SHELF) : shelf);
}

/**
 * The odds a whitetip reef shark is in the Reef's hectare on a day (M235): Maui Nui's whitetips (`whitetips.ts`, NOAA's
 * divers' count on the hard bottom to 30 m) a hectare of the reef's whole floor, as the simulated fish and the honu are
 * reckoned (`honuPerReefHa`); the Reef's alone, the count's 0 to 30 m. None where the site has none.
 */
export function whitetipVisitOdds(place: Place, habitat: string): number {
  const reef = reefOf(place);
  if (!place.sharks || !reef || habitat !== 'reef') return 0;
  const n = place.sharks.whitetip.counts.reduce((a, b) => a + b, 0);
  const reefM2 = stratumFloorKm2(reef).reduce((a, b) => a + b, 0) * 1e6;
  return Math.min(1, (n * REEF_AREA_M2) / Math.max(1, reefM2));
}

/**
 * The odds a reef manta is in the Reef's hectare on a day (M242): the share of NOAA's divers' site visits on Maui Nui's
 * reef that met one at all (`MANTA.visitsSeenShare`, 5 of 520, MEASURED, `REEF_SURVEYS`), a site visit taken for the
 * scene's day; no count of its numbers exists to spread over the reef (M223). The Reef's alone, the surveys' 0 to 30 m,
 * at Maui Nui alone, where they were counted (`siteHasReefResidents`).
 */
export function mantaVisitOdds(place: Place, habitat: string): number {
  return !place.visiting && siteHasReefResidents(place.siteId) && habitat === 'reef' ? MANTA.visitsSeenShare : 0;
}

/** The odds a honu, a hawksbill, a seal, a tiger shark, a whitetip (M235) and a manta (M242) visit a habitat's scene on a day, none where the site has none. */
export function visitOddsOf(place: Place, epochDay: number, habitat: string): VisitOdds {
  return { honu: place.turtles ? honuVisitOdds(place, epochDay, habitat) : 0, honuea: place.turtles ? honueaVisitOdds(place, epochDay, habitat) : 0, seal: sealVisitOdds(place, habitat), tigerShark: tigerSharkVisitOdds(place, habitat, epochDay), whitetip: whitetipVisitOdds(place, habitat), manta: mantaVisitOdds(place, habitat) };
}

/** A tiger shark of the day's (M197), or a whitetip (M235): a class drawn by its sharks, its stage an adult's from female maturity, its length its class's mean age's (`lengthAtAge`). */
function sharkVisitor(life: SharkLife, counts: readonly number[], epochDay: number, kind: 'tigerShark' | 'whitetip' = 'tigerShark'): Visitor {
  let u = hash3(epochDay, kind === 'whitetip' ? 235 : 4, 147) * Math.max(1, counts.reduce((a, b) => a + b, 0));
  let cls = counts.length - 1;
  for (let k = 0; k < counts.length; k++) {
    u -= counts[k] as number;
    if (u < 0) { cls = k; break; }
  }
  const age = Math.max(0, sharkAgeYears(life, cls, epochDay));
  return { kind, stage: age >= life.femaleMatureYears ? 'adult' : 'juvenile', lengthM: lengthAtAge(life, age) / 100, cls };
}

/**
 * A sea turtle of the day's at home (`honuSprites` at `perSprite` turtles each, the honu's 1,000 as the tank draws
 * them; the hawksbill's one each, too few for more), its stage and its carapace; null where none is.
 */
function turtleVisitor(life: TurtleLife, home: readonly number[], epochDay: number, perSprite: number, salt: number, adultsOnly: boolean): { adult: boolean; cm: number; cls?: number } | null {
  const sprites = honuSprites(life, home, epochDay, perSprite).filter((s) => !adultsOnly || s.adult);
  return sprites[Math.floor(hash3(epochDay, salt, 147) * sprites.length)] ?? null;
}

/** The honu, the hawksbills, the seals, the tiger sharks, the whitetips and the mantas visiting habitat `habitat` on day `epochDay`, each on its visit (`habitatVisitOn`, M266), its stage and size drawn on its visit's first day from the population as it is that day (a migrant honu an adult). */
export function visitorsOf(place: Place, epochDay: number, habitat: string): Visitor[] {
  const out: Visitor[] = [];
  // Each kind's visit covering the day (M266, `visitOn`, in place of a draw each day): drawn as it was on its first
  // day (its stage, its size, its class), so it is the same animal all its stay.
  const honu = place.turtles ? habitatVisitOn('honu', habitat, epochDay, (d) => honuVisitOdds(place, d, habitat)) : null;
  if (place.turtles && honu) {
    const s = turtleVisitor(HONU, place.turtles.honu.home, honu.start, HONU_DRAW.turtlesPerSprite, 1, habitat === 'blue');
    if (s) out.push({ kind: 'honu', stage: s.adult ? 'adult' : 'young', lengthM: s.cm / 100, cls: s.cls, visit: honu.start });
  }
  const honuea = place.turtles ? habitatVisitOn('honuea', habitat, epochDay, (d) => honueaVisitOdds(place, d, habitat)) : null;
  if (place.turtles && honuea) {
    const s = turtleVisitor(HONUEA, place.turtles.honuea.home, honuea.start, 1, 3, false);
    if (s) out.push({ kind: 'honuea', stage: s.adult ? 'adult' : 'young', lengthM: s.cm / 100, cls: s.cls, visit: honuea.start });
  }
  const seal = place.seals ? habitatVisitOn('seal', habitat, epochDay, () => sealVisitOdds(place, habitat)) : null;
  if (place.seals && seal) out.push({ ...sealVisitor(place.seals.monk, seal.start), visit: seal.start });
  // The Blue Water's of the sizes tracked off the shelf (M211's review).
  const shark = place.sharks ? habitatVisitOn('tigerShark', habitat, epochDay, (d) => tigerSharkVisitOdds(place, habitat, d)) : null;
  if (place.sharks && shark) out.push({ ...sharkVisitor(TIGER_SHARK, habitat === 'blue' ? offShelfCounts(place.sharks.tiger.counts, shark.start) : place.sharks.tiger.counts, shark.start), visit: shark.start });
  const whitetip = place.sharks ? habitatVisitOn('whitetip', habitat, epochDay, () => whitetipVisitOdds(place, habitat)) : null;
  if (place.sharks && whitetip) out.push({ ...sharkVisitor(WHITETIP_SHARK, place.sharks.whitetip.counts, whitetip.start, 'whitetip'), visit: whitetip.start });
  const manta = habitatVisitOn('manta', habitat, epochDay, () => mantaVisitOdds(place, habitat));
  if (manta) {
    // Its sex by the measured's (`MANTA.femaleShare`), its disc its sex's mean, and the hour it comes, each its own draw (M242).
    const female = hash3(manta.start, 242, 1) < MANTA.femaleShare;
    out.push({ kind: 'manta', stage: 'adult', lengthM: female ? MANTA.discM.female : MANTA.discM.male, female, arriveHour: mantaArrivalHour(hash3(manta.start, 242, 2)), visit: manta.start });
  }
  return out;
}

/** Each visiting kind's draw's salt (its kind's as the daily draw had them, M145 to M242, before M266's visits). */
const VISIT_SALT: Readonly<Record<VisitKind, number>> = { honu: 145, honuea: 196, seal: 146, tigerShark: 197, whitetip: 235, manta: 242 };

/** A kind's visit to a habitat's scene covering a day (M266, `visitOn`), by its odds there on each slot's first day. */
export function habitatVisitOn(kind: VisitKind, habitat: string, epochDay: number, odds: (epochDay: number) => number): Visit | null {
  let h = 0;
  for (const c of habitat) h = (h * 31 + c.charCodeAt(0)) % 1_000_003;
  return visitOn(VISIT_DAYS[kind], odds, epochDay, (h * 7 + VISIT_SALT[kind]) | 0);
}

/** A seal of the day's, its stage drawn by the seals at sea (`sealStages`, the nursing pups ashore), its length its stage's (`SEAL_BODY`). */
function sealVisitor(state: SealState, epochDay: number): Visitor {
  const n = sealStages(MONK_SEAL, state);
  const u = hash3(epochDay, 2, 147) * Math.max(1, n.pup + n.juvenile + n.adult);
  const stage: SealStage = u < n.pup ? 'pup' : u < n.pup + n.juvenile ? 'juvenile' : 'adult';
  // Its class within its stage by the seals of each (M203: a tag of it of its age), its own draw.
  const inStage = (c: number): boolean => (stage === 'pup' ? c === 0 : stage === 'juvenile' ? c > 0 && c < MONK_SEAL.firstPupYears : c >= MONK_SEAL.firstPupYears);
  const counts = state.counts.map((k, c) => (inStage(c) ? k : 0));
  let v = hash3(epochDay, 5, 147) * counts.reduce((a, b) => a + b, 0);
  let cls = 0;
  for (let c = 0; c < counts.length; c++) {
    if (!((counts[c] as number) > 0)) continue;
    cls = c;
    v -= counts[c] as number;
    if (v < 0) break;
  }
  return { kind: 'seal', stage, lengthM: SEAL_BODY.lengthM[stage], cls };
}

/**
 * The area a habitat's scene other than the Reef stands for, m², for its visitors' counts (M147, `src/sim/visits.ts`: a
 * scene's expected count is a species' density there times its area): a hectare, as the Reef's is (`REEF_AREA_M2`;
 * JUDGED, the drawing's, so a fish seen on the Reef and on the Slope is counted over the same floor).
 */
const VISIT_AREA_M2 = REEF_AREA_M2;

/**
 * The reef fish's home density, adults (initial and terminal) a square meter of Maui Nui's reef, and their phases' mean
 * lengths and the terminal males' share, by species: each species' count where it visits (M147), over the reef's floor
 * to 30 m, as the Reef's hectare holds them (`mauiNuiReefData`'s `perHa`).
 */
function reefFishHome(place: Place, epochDay: number): Map<FishSpeciesId, { perM2: number; initialM: number; terminalM: number; terminalShare: number }> {
  const out = new Map<FishSpeciesId, { perM2: number; initialM: number; terminalM: number; terminalShare: number }>();
  const reef = reefOf(place);
  if (!reef || !place.fish) return out;
  const reefM2 = mauiNuiReef(reef).strata.reduce((s, l) => s + l.floorKm2, 0) * 1e6;
  for (const life of REEF_FISH) {
    const c = fishCounts(life, (place.fish as NonNullable<Place['fish']>)[life.id], epochDay);
    const n = c.adults + c.terminal;
    out.set(life.id, { perM2: n / Math.max(1, reefM2), initialM: c.lengthMm.adults / 1000, terminalM: c.lengthMm.terminal / 1000, terminalShare: n > 0 ? c.terminal / n : 0 });
  }
  return out;
}

/**
 * The species visiting a habitat's scene on a day from their own habitats (M147, the user's word: "make it so that each
 * sea creature can have a chance to appear in other habitats as well, as long as it is scientifically realistic"), as
 * `src/sim/visits.ts` measures them: each one's mean count there (its density against home's, `SPECIES_VISITS`, times
 * its home's density, times the scene's hectare), its whole count today (`visitorCountOn`), and those drawn, each in its
 * slot before or after its swap (`crossingOf`), its phase drawn by its terminal males' share and its length its phase's
 * mean (the humpback's its home's density `humpbacksPerM2` of the whales in Hawaiʻi's waters that day, M147's science).
 * A species with no picture is counted and not drawn. The humpback over the reef was not drawn either until M210 (M147's
 * second review: a whale 12.37 m long, faded out as near as the Reef's camera can stand from it on a phone held upright,
 * swimming a lane shorter than itself); since M210 it is drawn at its true length on a lane as far off as fits it
 * (`WHALE_VISIT`).
 */
export function speciesVisits(world: World, place: Place, epochDay: number, habitat: string): { notes: SpeciesVisitNote[]; visitors: Visitor[] } {
  const home = reefFishHome(place, epochDay);
  const notes: SpeciesVisitNote[] = [];
  const visitors: Visitor[] = [];
  for (const species of Object.keys(SPECIES_VISITS) as VisitorSpecies[]) {
    const v = SPECIES_VISITS[species];
    const use = v.visits.find((u) => u.habitat === habitat);
    if (!use || v.home === habitat) continue;
    const whale = species === 'humpback';
    const fish = whale ? (place.whales ? { perM2: humpbacksPerM2(whaleCounts(HUMPBACK, place.whales.humpback, epochDay).inHawaii), initialM: 0, terminalM: 0, terminalShare: 0 } : undefined) : home.get(species);
    if (!fish) continue;
    const expected = use.value * fish.perM2 * VISIT_AREA_M2;
    const count = visitorCountOn(expected, world.seed, epochDay, species, habitat as VisitHabitat);
    const notDrawn = whale || FISH_KINDS.some((k) => k.species === species) ? null : 'no picture of it yet';
    notes.push({ species, home: v.home, perAreaOfHome: use.value, expected, count, notDrawn });
    if (notDrawn) continue;
    for (let index = 0; index < count; index++) {
      const crossing = crossingOf(world.seed, epochDay, species, habitat as VisitHabitat, index);
      if (!crossing) continue;
      for (const after of [false, true]) {
        const slot = { day: epochDay, index, after, hour: crossing.hour, home: crossing.home };
        // A humpback at its true length (M210), its juveniles' and adults' mean.
        if (whale) {
          visitors.push({ kind: species, stage: 'adult', lengthM: PELAGIC_SCENE.whaleM, slot });
          continue;
        }
        const terminal = hash3(epochDay, index * 2 + (after ? 1 : 0), 14_700 + REEF_FISH.findIndex((l) => l.id === species)) < fish.terminalShare;
        visitors.push({ kind: species, stage: terminal ? 'terminal' : 'adult', lengthM: terminal ? fish.terminalM : fish.initialM, slot });
      }
    }
  }
  return { notes, visitors };
}

/** The visitors' kinds a tag's kind is drawn as (M203). */
const TAGGED_KINDS: Partial<Record<TagSpecies, 'honu' | 'honuea' | 'seal' | 'tigerShark'>> = { honu: 'honu', honuea: 'honuea', monkSeal: 'seal', tigerShark: 'tigerShark' };

/**
 * The tagged honu, hawksbills, seals and tiger sharks in a habitat today (M203, the user's word: "what if i made it so
 * that the ones that are uncommon or rarely seen instead are trackable when they leave a certain biome into different
 * ones?"): each living one at the player's own sanctuary whose tag puts it there today (`tagPlaceOn`), drawn as a visitor
 * of its kind at its own age's size (a turtle's carapace its class's, a shark's length its age's, a seal's its stage's).
 * None at a site visited.
 */
export function taggedVisitorsOf(world: World, place: Place, epochDay: number, habitat: string): Visitor[] {
  if (place.visiting) return [];
  const out: Visitor[] = [];
  for (const a of world.tags.animals) {
    const kind = TAGGED_KINDS[a.species];
    if (!kind || a.siteId !== place.siteId || tagPlaceOn(world, a, epochDay) !== habitat) continue;
    const tag = { id: a.id, name: a.name };
    if (kind === 'honu' || kind === 'honuea') {
      const life = kind === 'honu' ? HONU : HONUEA;
      const adult = stagesOn(life, epochDay)[a.cls] === 'adult';
      out.push({ kind, stage: adult ? 'adult' : 'young', lengthM: carapaceCm(life, turtleAgeYears(life, a.cls, epochDay)) / 100, tag });
    } else if (kind === 'tigerShark') {
      const age = Math.max(0, sharkAgeYears(TIGER_SHARK, a.cls, epochDay));
      out.push({ kind, stage: age >= TIGER_SHARK.femaleMatureYears ? 'adult' : 'juvenile', lengthM: lengthAtAge(TIGER_SHARK, age) / 100, tag });
    } else {
      const stage: SealStage = a.cls === 0 ? 'pup' : a.cls < MONK_SEAL.firstPupYears ? 'juvenile' : 'adult';
      out.push({ kind, stage, lengthM: SEAL_BODY.lengthM[stage], tag });
    }
  }
  return out;
}

/**
 * A scene's visitors, the honu's and the seals' with the tagged (M203) and the species' (M147), and the species' notes:
 * each tagged one drawn on every day its tag puts it there, whatever the odds of one of its kind coming (the tag's point,
 * as a tagged animal is found where its fixes put it: M203's review MEASURED a tagged hawksbill on the Reef 1.0 days in
 * 1 against its kind's 0.0076), in the place of the day's visitor of its kind where one came, so the two are never
 * drawn side by side, and beside the day's where none did. Untagged, each kind as rare as its odds.
 */
function withSpecies(world: World, place: Place, epochDay: number, habitat: string, honuAndSeals: Visitor[]): { visitors: Visitor[]; visits: SpeciesVisitNote[] } {
  const { notes, visitors } = speciesVisits(world, place, epochDay, habitat);
  const tagged = taggedVisitorsOf(world, place, epochDay, habitat);
  const day = honuAndSeals.filter((v) => !tagged.some((t) => t.kind === v.kind));
  // Each tagged one standing in for the day's visitor of its kind it took the place of, as drawn (M203's review).
  const standing = tagged.map((t) => {
    const was = honuAndSeals.find((v) => v.kind === t.kind);
    return was && tagged.find((x) => x.kind === t.kind) === t ? { ...t, from: visitorId(was) } : t;
  });
  return { visitors: [...day, ...standing, ...visitors], visits: notes };
}

/**
 * Maui Nui Reef as one island's reef (M287, `docs/CONSERVATION_PROGRESS.md` 5.3 and 5.4): every stratum given that
 * island's own at the same depth (its live coral, its turf and macroalgae) and every island's fish its density, so the
 * scene the player opens from an island's card is driven by that island's simulated state; the layout, the depths'
 * places and the bleaching by depth (the simulation's by depth alone) as the whole reef's. A school's card names its
 * home island and that island's own density, so each island's fish stay its own (review M287's: every card had told the
 * focus island's under the others' names). The counted species, the octopuses, the honu and the visitors stay Maui Nui's.
 */
export function focusIsland(data: MauiNuiReefData, island: ReefIsland): MauiNuiReefData {
  const f = REEF_ISLANDS.indexOf(island);
  const strata = data.reef.strata;
  const own = (values: readonly number[]): number[] => strata.map((l) => {
    const j = strata.findIndex((m) => m.islandIndex === f && m.binIndex === l.binIndex);
    return j >= 0 ? (values[j] as number) : (values[strata.indexOf(l)] as number);
  });
  const shares = data.islands.map((x) => x.areaShare);
  const sf = shares[f] as number;
  // Each island's share of the hectare at the focus island's density: its hectare's fish over its reef's share, laid
  // over every island's share, rounded together.
  const spread = (exact: readonly number[]): number[] => shares.map((a) => (sf > 0 ? ((exact[f] ?? 0) * a) / sf : 0));
  const fish = data.fish.map((x) => {
    const exact = { initial: spread(x.exact.initial), terminal: spread(x.exact.terminal), young: spread(x.exact.young) };
    const sum = (v: readonly number[]): number => v.reduce((a, b) => a + b, 0);
    return {
      ...x,
      initial: largestRemainder(exact.initial), terminal: largestRemainder(exact.terminal), young: largestRemainder(exact.young),
      perHa: sum(exact.initial) + sum(exact.terminal),
      youngPerHa: sum(exact.young),
      exact,
    };
  });
  return {
    ...data,
    coverPercent: own(data.coverPercent),
    ...(data.bottom ? { bottom: { turfPercent: own(data.bottom.turfPercent), macroalgaePercent: own(data.bottom.macroalgaePercent) } } : {}),
    fish,
    focus: island,
  };
}
